import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTransactions, useTransactionCategories } from "@/hooks/use-transactions";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { getChartColors, getCategoryColor } from "@/lib/utils";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
} from "recharts";

interface ExpenseBreakdownChartProps {
  isLoading?: boolean;
}

export function ExpenseBreakdownChart({ isLoading = false }: ExpenseBreakdownChartProps) {
  const [chartData, setChartData] = useState<any[]>([]);
  
  // Get current month range
  const today = new Date();
  const startDate = startOfMonth(today);
  const endDate = endOfMonth(today);
  
  // Format dates for API
  const startDateStr = format(startDate, "yyyy-MM-dd");
  const endDateStr = format(endDate, "yyyy-MM-dd");
  
  // Categories from COA (useTransactionCategories returns COA accounts)
  const { categories } = useTransactionCategories();
  const { transactions } = useTransactions(undefined, {
    startDate: startDateStr,
    endDate: endDateStr,
    type: "expense",
    limit: 1000,
  });
  
  useEffect(() => {
    if (!transactions || !transactions.length || !categories.length) {
      setChartData([]);
      return;
    }

    const categoryTotals: Record<string, number> = {};
    let uncategorizedTotal = 0;
    let totalExpenses = 0;

    transactions.forEach((transaction: any) => {
      const amount = parseFloat(transaction.amount.toString());
      totalExpenses += amount;
      const resolvedId = transaction.coaAccountId || transaction.coa_account_id || transaction.categoryId || transaction.category_id;
      if (resolvedId) {
        const match = categories.find((cat: any) => cat.id === resolvedId);
        const name = match?.name || `Account #${resolvedId}`;
        categoryTotals[name] = (categoryTotals[name] || 0) + amount;
      } else {
        uncategorizedTotal += amount;
      }
    });

    const data = Object.entries(categoryTotals).map(([name, value]) => ({
      name,
      value,
      percentage: totalExpenses ? ((value / totalExpenses) * 100).toFixed(1) : 0,
    }));

    if (uncategorizedTotal > 0) {
      data.push({
        name: "Uncategorized",
        value: uncategorizedTotal,
        percentage: totalExpenses ? ((uncategorizedTotal / totalExpenses) * 100).toFixed(1) : 0,
      });
    }

    data.sort((a, b) => b.value - a.value);
    const topFiveData = data.slice(0, 5);

    if (data.length > 5) {
      const othersTotal = data.slice(5).reduce((sum, item) => sum + item.value, 0);
      if (othersTotal > 0) {
        topFiveData.push({
          name: "Others",
          value: othersTotal,
          percentage: totalExpenses ? ((othersTotal / totalExpenses) * 100).toFixed(1) : 0,
        });
      }
    }

    setChartData(topFiveData);
  }, [transactions, categories]);
  
  const chartColors = getChartColors();
  
  // Custom renderer for pie chart labels
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
  }: any) => {
    // Only show labels for sections with significant percentage
    if (percent < 0.05) return null;
    
    const radius = outerRadius * 0.8;
    const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
    const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));
    
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="medium"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Expense Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-80 w-full bg-neutral-100 animate-pulse rounded-md"></div>
        </CardContent>
      </Card>
    );
  }
  
  if (!chartData.length) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Expense Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-80 flex items-center justify-center">
            <p className="text-neutral-500">No expenses recorded this month</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">Expense Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-80 flex items-center gap-6">
          {/* Categories Legend - Left Side */}
          <div className="flex-1">
            <div className="space-y-3">
              {chartData.map((entry, index) => (
                <div key={entry.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: chartColors[index % chartColors.length] }}
                    />
                    <span className="text-sm font-medium">{entry.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(Number(entry.value))}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entry.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Chart - Right Side */}
          <div className="w-64 h-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={chartColors[index % chartColors.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => 
                    new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(Number(value))
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}